import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Calendar, Euro, MapPin, AlertCircle, CheckCircle, ExternalLink } from "lucide-react";

const subsidies = [
  {
    name: "ISDE Subsidie 2025",
    type: "Rijkssubsidie",
    amount: "Tot €3.000",
    category: "warmtepomp",
    deadline: "31 december 2025",
    description: "Investeringssubsidie Duurzame Energie voor warmtepompen",
    requirements: [
      "Woningeigenaar",
      "Vervanging gas-CV ketel",
      "Gecertificeerde installateur",
      "Minimaal warmtepomp rendement COP 4.0"
    ],
    eligible: ["Particuliere woningen", "VvE's", "Huurwoningen (eigenaar)"],
    lokaal: {
      arnhem: "Extra gemeentelijke stimulering €500",
      nijmegen: "Verduurzamingslening tot €25.000",
      elst: "Lokale duurzaamheidsbonus €200"
    }
  },
  {
    name: "Zonnepanelen BTW Voordeel",
    type: "Belastingvoordeel",
    amount: "21% BTW teruggave",
    category: "zonnepanelen",
    deadline: "Doorlopend",
    description: "0% BTW op zonnepanelen installaties tot 10kWp",
    requirements: [
      "Particuliere woning",
      "Maximaal 10kWp vermogen",
      "Professionele installatie",
      "Correcte BTW aangifte"
    ],
    eligible: ["Woningeigenaren", "Particuliere installaties"],
    lokaal: {
      arnhem: "Snelleadpaal subsidie €750",
      nijmegen: "Gratis energiescan bij zonnepanelen",
      elst: "Combinatievoordeel met warmtepomp €300"
    }
  },
  {
    name: "Laadpaal Subsidie",
    type: "Lokale subsidie",
    amount: "Tot €750",
    category: "laadpaal",
    deadline: "Op = Op basis",
    description: "Subsidie voor thuislaadpunten elektrische auto's",
    requirements: [
      "Elektrische auto in bezit",
      "Particuliere oprit/garage",
      "Smart charging functionaliteit",
      "Erkende installateur"
    ],
    eligible: ["Particuliere woningeigenaren", "VvE bewoners"],
    lokaal: {
      arnhem: "€750 subsidie per laadpaal",
      nijmegen: "€500 + gratis installatie-inspectie",
      elst: "€400 lokale subsidie beschikbaar"
    }
  },
  {
    name: "Thuisbatterij Stimulering",
    type: "Energiebelasting korting",
    amount: "€0.057 per kWh",
    category: "batterij",
    deadline: "Doorlopend 2025",
    description: "Salderingsregeling en energiebelasting voordeel",
    requirements: [
      "Geregistreerde thuisbatterij",
      "Gekoppeld aan zonnepanelen",
      "Smart meter installatie",
      "Netcode registratie"
    ],
    eligible: ["Woningeigenaren met zonnepanelen"],
    lokaal: {
      arnhem: "Pilot project extra voordeel €200",
      nijmegen: "Gratis batterij-check bij zonnepanelen",
      elst: "Combinatievoordeel met andere duurzame oplossingen"
    }
  }
];

const cities = [
  { key: 'arnhem', name: 'Arnhem', extra: 'Duurzaamheidsfonds beschikbaar' },
  { key: 'nijmegen', name: 'Nijmegen', extra: 'Green Deal voordelen' },
  { key: 'elst', name: 'Elst', extra: 'Lokale combinatievoordelen' }
];

export function SubsidyGuide2025() {
  const [selectedCity, setSelectedCity] = React.useState<string>('arnhem');

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <Euro className="w-6 h-6 text-primary" />
          <h2 className="text-2xl font-bold">Subsidiegids 2025</h2>
        </div>
        <p className="text-muted-foreground">
          Alle beschikbare subsidies en voordelen voor duurzame energie in uw regio
        </p>
      </div>

      {/* Stad selectie */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Lokale Voordelen
          </CardTitle>
          <CardDescription>
            Selecteer uw stad voor specifieke lokale subsidies en voordelen
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-3">
            {cities.map((city) => (
              <div
                key={city.key}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedCity === city.key
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setSelectedCity(city.key)}
              >
                <div className="font-medium">{city.name}</div>
                <div className="text-sm text-muted-foreground">{city.extra}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Subsidie overzicht */}
      <div className="grid gap-6">
        {subsidies.map((subsidy, index) => (
          <Card key={index}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {subsidy.name}
                    <Badge variant="secondary">{subsidy.type}</Badge>
                  </CardTitle>
                  <CardDescription>{subsidy.description}</CardDescription>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">{subsidy.amount}</div>
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {subsidy.deadline}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="requirements">
                  <AccordionTrigger>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Voorwaarden
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2">
                      {subsidy.requirements.map((req, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{req}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="eligible">
                  <AccordionTrigger>
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Wie komt in aanmerking?
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2">
                      {subsidy.eligible.map((eligible, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{eligible}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="local">
                  <AccordionTrigger>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Lokale Voordelen - {cities.find(c => c.key === selectedCity)?.name}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="p-3 bg-primary/5 rounded-lg">
                      <p className="text-sm">
                        {subsidy.lokaal[selectedCity as keyof typeof subsidy.lokaal]}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <div className="flex gap-2 pt-2">
                <Button size="sm">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Meer Info
                </Button>
                <Button variant="outline" size="sm">
                  Aanvragen
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Belangrijke data */}
      <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
            <AlertCircle className="w-5 h-5" />
            Belangrijke Deadlines 2025
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="text-sm space-y-1">
            <div><strong>31 maart 2025:</strong> ISDE ronde 1 deadline</div>
            <div><strong>30 juni 2025:</strong> Lokale laadpaal subsidies (beperkt budget)</div>
            <div><strong>31 oktober 2025:</strong> ISDE ronde 2 deadline</div>
            <div><strong>31 december 2025:</strong> BTW voordeel zonnepanelen eindigt mogelijk</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}